/* Ownership resolution (CID v1_495).
 *
 * Walking a group structure to its natural persons is a search problem with a
 * price per step: every register check costs credits. The regulation supplies
 * the stopping rule, so the engine and the budget agree by construction.
 *
 * MLR 2017 reg 5 defines a beneficial owner as holding more than 25% of the
 * shares or voting rights, DIRECTLY OR INDIRECTLY. Indirect holdings multiply
 * down the chain, so a 30% holder of a parent that holds 40% of the applicant
 * holds 12% effectively and is not a beneficial owner on that path. Holdings
 * SUM across paths where the same party appears more than once, which is the
 * case a hand-drawn chart usually misses.
 *
 * The frontier is therefore: corporate entities in the structure whose own
 * ownership is not yet resolved AND whose effective holding could still reach
 * the threshold. Everything outside it is recorded as below threshold, which
 * is a decision with a reason on the file rather than an unexplained gap.
 */

const norm = (v) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ')
  .replace(/[.,]/g, '').replace(/\b(limited|ltd|plc|llp|llc|inc|corp|corporation|gmbh|ag|sa|sarl|bv|nv|oy|ab|aps|pte|pty)\b/g, '').trim();

/** Percentage from a string or number; null where the source says nothing. */
/**
 * v1_771: is this percentage a FLOOR ("75 percent or more", "more than 50%",
 * "at least 25%")? A Companies House PSC register states bands, not holdings;
 * pctOf takes the lower bound, and the file must then say "or more" rather than
 * present the bound as the holding (walk runs 7 to 12 showed "75%").
 */
export function pctIsFloor(v) {
  if (v == null || typeof v === 'number') return false;
  return /\bor more\b|\bmore than\b|\bat least\b|\bover\b|≥|>=|\bor above\b/i.test(String(v));
}

export function pctOf(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) && v >= 0 && v <= 100 ? v : null;
  const s = String(v);
  // A range ("25-50%") is treated at its LOWER bound: the file may not claim
  // more than the register asserts. A "more than 75%" style band takes 75.
  const range = s.match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)/);
  if (range) return Number(range[1]);
  const one = s.match(/(\d+(?:\.\d+)?)/);
  if (!one) return null;
  const n = Number(one[1]);
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
}

const CORP = /\b(ltd|limited|plc|llp|llc|inc|corp|corporation|company|holdings?|group|gmbh|ag|sa|sarl|bv|nv|oy|ab|aps|se|kg|ug|pte|pty|trust|foundation|nominees?|partners?|capital|investments?)\b/i;
// Registers carry annotations and institutional entries that are plainly not
// natural persons even without a company suffix: a registrar line, a pension
// scheme, a trustee body. Treating one as an individual would promote it to
// beneficial owner and end the walk on a fiction, so these are never natural.
const NOT_NATURAL = /\b(register|registrar|registered|held by|trustees?|pension|scheme|fund|bank|securities|nominee|custodian|depositar|treasury|employee|share plan|esop|plc|equiniti|computershare|link asset|associates|ventures|management|services|international|worldwide|global|enterprises?|industries|properties|estates)\b/i;
export function looksCorporate(name, declaredType) {
  if (declaredType === 'entity' || declaredType === 'corporate') return true;
  const n = String(name || '').trim();
  if (CORP.test(n) || NOT_NATURAL.test(n)) return true;
  if (declaredType === 'individual' || declaredType === 'person') return false;
  // A natural person's name is a handful of words with no institutional
  // vocabulary. Anything longer is treated as an entity: over-classifying as
  // corporate keeps the entity ON the frontier for the officer to resolve,
  // whereas under-classifying would silently declare a UBO.
  const words = n.split(/\s+/).filter(Boolean);
  return words.length > 5;
}

/**
 * Resolve a structure.
 *
 * links: [{ holder, held, percentage, holderType, source, verified }]
 *   held === applicant for a direct holding of the client.
 * resolved: names whose OWN ownership has been established (a register check
 *   returned its owners, or the officer recorded it as resolved).
 * threshold: the beneficial-ownership threshold in percent (25 by default,
 *   lower it for higher-risk cases where the firm's policy requires it).
 *
 * Returns effective holdings per party, the frontier ranked by what is still
 * at stake, and the parties that qualify as beneficial owners on the arithmetic.
 */
export function resolveOwnership({ applicant, links = [], resolved = [], threshold = 25, maxDepth = 8 } = {}) {
  const app = norm(applicant);
  const resolvedSet = new Set((resolved || []).map(norm));
  // A party name must be a name (v1_504). Extraction has been seen to emit a
  // holder field containing the holder, the entity held, the party type and an
  // address in one string, sometimes in scrambled order. Such a link cannot be
  // repaired reliably, and computing on it produces confident nonsense: an
  // effective holding against the wrong company. So it is quarantined, and the
  // panel says the chain needs rebuilding rather than showing arithmetic.
  const malformedName = (v) => {
    const t = String(v || '');
    if (t.length > 90) return true;
    if (/\s[—–]\s*in\s/i.test(t)) return true;                       // "X — in Y"
    const opens = (t.match(/\(/g) || []).length, closes = (t.match(/\)/g) || []).length;
    if (opens !== closes) return true;                                  // mid-sentence fragment
    if (/\((?:individual|entity|corporate)\b/i.test(t) && /,/.test(t)) return true;
    return false;
  };
  const malformed = (links || []).filter(l => l && (malformedName(l.holder) || malformedName(l.held)));
  const clean = (links || []).filter(l => l && l.holder && l.held && norm(l.holder) !== norm(l.held)
    && !malformedName(l.holder) && !malformedName(l.held));

  // held -> holders, for walking upward from the applicant.
  const holdersOf = new Map();
  const display = new Map();
  for (const l of clean) {
    const h = norm(l.held), o = norm(l.holder);
    display.set(o, display.get(o) || String(l.holder).trim());
    display.set(h, display.get(h) || String(l.held).trim());
    if (!holdersOf.has(h)) holdersOf.set(h, []);
    holdersOf.get(h).push(l);
  }

  const effective = new Map();   // party -> effective % of the applicant
  const paths = new Map();       // party -> [[path descriptions]]
  const unknownPct = new Map();  // party -> true where a link on its path had no percentage
  const floorPct = new Map();    // party -> true where a link on its path is a band floor (v1_771)
  // v1_867: voting rights, kept apart from the equity share. A link's votes are
  // its stated votingPercentage, else its equity share (votes follow shares
  // unless a document says otherwise). Carried along the path the same way as
  // equity. votesStated marks a party whose path has a stated voting figure,
  // the only case where the votes can differ from the equity share.
  const effectiveVotes = new Map();
  const votesStated = new Map();

  const walk = (nodeKey, carried, depth, trail, sawUnknown, linkTrail, sawFloor = false, carriedVotes = carried, sawVotes = false) => {
    if (depth > maxDepth) return;
    const ups = holdersOf.get(nodeKey) || [];
    for (const l of ups) {
      const holderKey = norm(l.holder);
      if (trail.includes(holderKey)) continue; // circular holding: stop, never loop
      const p = pctOf(l.percentage);
      // An unrecorded percentage cannot be assumed: the path stays live and is
      // flagged, because "we do not know how much" is not "we know it is small".
      const share = p == null ? carried : carried * (p / 100);
      const nextUnknown = sawUnknown || p == null;
      effective.set(holderKey, (effective.get(holderKey) || 0) + (p == null ? 0 : share));
      const vStated = pctOf(l.votingPercentage);
      const v = vStated != null ? vStated : p;
      const voteShare = v == null ? carriedVotes : carriedVotes * (v / 100);
      effectiveVotes.set(holderKey, (effectiveVotes.get(holderKey) || 0) + (v == null ? 0 : voteShare));
      const nextVotes = sawVotes || vStated != null;
      if (nextVotes) votesStated.set(holderKey, true);
      if (nextUnknown) unknownPct.set(holderKey, true);
      const nextFloor = sawFloor || (p != null && pctIsFloor(l.percentage));
      if (nextFloor) floorPct.set(holderKey, true);
      const desc = [...trail.slice(1).map(k => display.get(k) || k), display.get(holderKey) || l.holder];
      // The FULL link chain, holder-most-distant first, so the relationship can
      // be stated in words rather than left for the reader to infer from a
      // diagram. This is what makes the panel readable by someone who did not
      // build the case.
      const chain = [...linkTrail, { holder: String(l.holder).trim(), held: String(l.held).trim(), percentage: l.percentage ?? null, pct: p, floor: p != null && pctIsFloor(l.percentage), verified: !!l.verified, source: l.source || null, ...(vStated != null ? { votingPercentage: vStated } : {}) }];
      if (!paths.has(holderKey)) paths.set(holderKey, []);
      paths.get(holderKey).push({ via: desc, percentage: p, effective: p == null ? null : share, chain, depth: chain.length });
      walk(holderKey, share, depth + 1, [...trail, holderKey], nextUnknown, chain, nextFloor, voteShare, nextVotes);
    }
  };
  walk(app, 100, 0, [app], false, []);

  // MAJORITY CONTROL THROUGH AN INTERMEDIATE (v1_874). A natural person who
  // controls more than 50 percent of the votes of an intermediate is treated as
  // controlling all of that intermediate's votes in the entity below, as in the
  // PSC regime's majority stake rule; it applies again at each level down. The
  // equity share stays multiplied. Worked top down along each path: the person's
  // share of the intermediate's votes, and where it is above half, the whole of
  // the intermediate's votes in the next entity. A path with a missing figure
  // keeps the multiplied votes (nothing is assumed). Returns the votes in the
  // applicant along the path and the intermediates whose votes were looked through.
  const controlVotesOnPath = (chain) => {
    if (!Array.isArray(chain) || chain.length < 2) return null;
    const voteOf = (st) => { const v = pctOf(st.votingPercentage); return v != null ? v : st.pct; };
    const top = chain[chain.length - 1];
    let r = voteOf(top);
    if (r == null) return null;
    r = r / 100;
    const through = [];
    for (let i = chain.length - 2; i >= 0; i--) {
      const v = voteOf(chain[i]);
      if (v == null) return null;
      if (r > 0.5) { if (r < 1) through.push(chain[i].holder); r = 1; }
      r = r * (v / 100);
    }
    return { votes: r * 100, through };
  };

  const parties = [];
  for (const [key, pct] of effective) {
    const name = display.get(key) || key;
    const anyLink = clean.find(l => norm(l.holder) === key);
    const corporate = looksCorporate(name, anyLink && anyLink.holderType);
    const isResolved = resolvedSet.has(key);
    const uncertain = !!unknownPct.get(key);
    const myPaths = paths.get(key) || [];
    // v1_867: the beneficial owner test reads the higher of the equity share
    // and the voting rights (MLR 2017 reg 5: shares or voting rights).
    let votes = votesStated.get(key) ? Math.round((effectiveVotes.get(key) || 0) * 100) / 100 : null;
    // v1_874: a natural person's votes with majority control looked through.
    let controlThrough = [];
    if (!corporate && myPaths.length) {
      const per = myPaths.map(pp => controlVotesOnPath(pp.chain));
      if (per.every(x => x)) {
        const through = [...new Set(per.flatMap(x => x.through))];
        if (through.length) {
          votes = Math.round(Math.min(100, per.reduce((a, x) => a + x.votes, 0)) * 100) / 100;
          controlThrough = through;
        }
      }
    }
    const testPct = votes != null ? Math.max(pct, votes) : pct;
    const testedOn = votes != null && votes > pct ? 'votes' : 'equity';
    const shortest = myPaths.slice().sort((x, y) => (x.depth || 99) - (y.depth || 99))[0] || null;
    // "How does this party connect to the client?" answered as a sentence.
    const relationship = (() => {
      if (!shortest || !shortest.chain || !shortest.chain.length) return null;
      const steps = shortest.chain.slice().reverse(); // nearest the holder first
      const part = steps.map((st, idx) => {
        const amt = st.pct != null ? st.pct + '%' + (st.floor ? ' or more' : '') : 'an unstated share';
        const of = idx === steps.length - 1 ? 'the applicant' : st.held;
        return `${amt} of ${of}`;
      });
      return 'Holds ' + part.join(', which holds ');
    })();
    parties.push({
      key, name, corporate,
      depth: shortest ? shortest.depth : 1,
      relationship,
      pathCount: myPaths.length,
      // Where this party was discovered, so a further check searches the right
      // register rather than whatever is in the officer's search box.
      iso: (anyLink && anyLink.iso) || null,
      effective: Math.round(pct * 100) / 100,
      votes,
      testedOn,
      // v1_874: the intermediates whose votes were counted in full (majority control).
      ...(controlThrough.length ? { controlThrough } : {}),
      // v1_771: the effective share is a lower bound (a band on the path).
      atLeast: !!floorPct.get(key),
      uncertain,
      resolved: isResolved,
      paths: paths.get(key) || [],
      verified: (clean.filter(l => norm(l.holder) === key).every(l => !!l.verified)) && (paths.get(key) || []).length > 0,
      // v1_830: at or above the threshold (one rule everywhere).
      beneficialOwner: !corporate && (testPct >= threshold || uncertain),
      // A corporate holder is on the frontier when its own owners are unknown
      // and its effective holding could still carry a beneficial owner: above
      // the threshold, or unknown because a percentage on its path is missing.
      frontier: corporate && !isResolved && (testPct >= threshold || uncertain),
      belowThreshold: corporate && !isResolved && !uncertain && testPct < threshold
    });
  }
  parties.sort((a, b) => (b.uncertain ? 1 : 0) - (a.uncertain ? 1 : 0) || b.effective - a.effective);

  // ── Integrity of the structure itself (v1_497) ───────────────────────
  // Direct holdings of one company cannot exceed 100%. When they do, the
  // sources are describing different structures: usually the client's
  // declaration against the register, occasionally a stale register. It is
  // arithmetic, so it is certain, and it is the most important thing an
  // officer can be told about an ownership picture, because every effective
  // holding computed from it is unsafe until it is explained.
  const directLinks = clean.filter(l => norm(l.held) === app);
  const declaredSrc = (l) => !/register|extract|kyckr/i.test(String(l.source || ''));
  let directTotal = 0;
  for (const l of directLinks) { const p = pctOf(l.percentage); if (p != null) directTotal += p; }
  directTotal = Math.round(directTotal * 100) / 100;
  const declaredHolders = [...new Set(directLinks.filter(declaredSrc).map(l => String(l.holder).trim()))];
  const registerHolders = [...new Set(directLinks.filter(l => !declaredSrc(l)).map(l => String(l.holder).trim()))];
  const onlyDeclared = declaredHolders.filter(n => !registerHolders.some(r => norm(r) === norm(n)));
  const onlyRegister = registerHolders.filter(n => !declaredHolders.some(d => norm(d) === norm(n)));
  const integrity = {
    malformedLinks: malformed.length,
    malformedExamples: malformed.slice(0, 3).map(l => String(l.holder).slice(0, 90)),
    directTotal,
    overSubscribed: directTotal > 100.5,
    sourceConflict: declaredHolders.length > 0 && registerHolders.length > 0 && (onlyDeclared.length > 0 || onlyRegister.length > 0),
    onlyDeclared, onlyRegister,
    note: malformed.length
      ? `${malformed.length} ownership link${malformed.length === 1 ? '' : 's'} could not be read: the holder name contains the entity held, the party type or an address rather than a name, so the link cannot be trusted and has been left out. Rebuild the analysis (Reprocess) before relying on the structure; if it recurs, the extraction needs correcting.`
      : directTotal > 100.5
      ? `Direct holdings of the applicant total ${directTotal}%, which cannot be correct: the sources are describing different structures. Resolve this before relying on any effective holding below.`
      : (declaredHolders.length > 0 && registerHolders.length > 0 && (onlyDeclared.length > 0 || onlyRegister.length > 0)
        ? `The file and the register name different direct holders${onlyDeclared.length ? '; on the file only: ' + onlyDeclared.join(', ') : ''}${onlyRegister.length ? '; at the register only: ' + onlyRegister.join(', ') : ''}. One of them is wrong, or the holding is indirect; the client explains.`
        : null)
  };

  const frontier = parties.filter(p => p.frontier);
  const ubos = parties.filter(p => p.beneficialOwner);
  const parked = parties.filter(p => p.belowThreshold);
  return {
    threshold,
    integrity,
    parties,
    frontier,
    beneficialOwners: ubos,
    parkedBelowThreshold: parked,
    complete: frontier.length === 0,
    // What an officer needs in one sentence.
    summary: frontier.length === 0
      ? (ubos.length
        ? `Structure resolved to natural persons: ${ubos.map(u => u.name + ' ' + (u.uncertain ? '(holding not stated)' : u.effective + '%')).join(', ')}.`
        : 'No party reaches the beneficial-ownership threshold on the structure as recorded.')
      : `${frontier.length} corporate holder${frontier.length === 1 ? '' : 's'} still to resolve: ${frontier.slice(0, 4).map(f => f.name + ' ' + (f.uncertain ? '(holding not stated)' : f.effective + '% effective')).join(', ')}${frontier.length > 4 ? ' and others' : ''}.`
  };
}

/** Cost of the checks already spent on a structure, grouped by subject. */
export function structureSpend(checks = []) {
  const bySubject = new Map();
  let total = 0, credits = 0;
  for (const c of checks) {
    const v = Number(c.cost_value);
    if (Number.isFinite(v)) { total += v; if ((c.cost_type || '').toLowerCase().includes('credit')) credits += v; }
    const k = c.subject_name || c.company_name || c.kyckr_id || 'applicant';
    const cur = bySubject.get(k) || { subject: k, checks: 0, cost: 0 };
    cur.checks += 1;
    if (Number.isFinite(v)) cur.cost += v;
    bySubject.set(k, cur);
  }
  return { total, credits, subjects: [...bySubject.values()].sort((a, b) => b.cost - a.cost) };
}
